import stringUtils from '../../../../qti/utils/StringUtils';
import a11yIIBScreenReaderService, { A11yForIIBScreenReaderIteration } from '../../../../services/a11yIIBScreenReaderService';
import { t } from '../../../../services/i18nService';

const isA11yIterationForIIBScreenReaderEnabled = () => {
    return a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported();
};

const extendedTextA11ySelector = {
    /**
     * Gets accessibility attributes for the essay question group container
     * @param {object} model - The interaction model
     * @param {object} questionModel - The question model
     * @returns {object} Object containing aria attributes for the essay question group
     */
    getQuestionGroupA11yAttributes: (model, questionModel) => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                'role': undefined,
                'aria-label': undefined,
            };
        }

        // Generate dynamic question title from prompt or fallback
        const questionTitle = model.prompt?.content || `${t('generic.questionNumberPrefix')} ${questionModel.questionNumberingInSection || ''}`;

        return {
            'role': 'group',
            'aria-label': t('interactions.extendedText.questionGroupAriaLabel', {
                questionTitle: stringUtils.stripHtml(questionTitle), // Strip HTML tags
            }),
        };
    },

    /**
     * Gets accessibility attributes for the rich text answer group
     * @param {object} model - The interaction model
     * @param {object} questionModel - The question model
     * @returns {object} Object containing aria attributes for the rich text answer group
     */
    getRichTextAnswerGroupA11yAttributes: (model, questionModel) => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                'role': undefined,
                'aria-label': undefined,
            };
        }

        const answerLabel = questionModel.questionNumberingInSection
            ? `${questionModel.questionNumberingInSection}(a)`
            : 'Answer';

        return {
            'role': 'group',
            'aria-label': t('interactions.extendedText.richTextAnswerGroupAriaLabel', { answerLabel }),
        };
    },

    /**
     * Gets accessibility instructions for keyboard navigation
     * @returns {string|undefined} Keyboard instruction text or undefined
     */
    getKeyboardInstructions: () => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return undefined;
        }

        return t('interactions.extendedText.keyboardInstructions');
    },

    /**
     * Gets accessibility attributes for the help button
     * @returns {object} Object containing aria attributes for the help button
     */
    getHelpButtonA11yAttributes: () => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                'aria-label': t('interactions.keyboardHelp'),
            };
        }

        return {
            'aria-label': t('interactions.extendedText.helpButtonAriaLabel'),
        };
    },

    /**
     * Gets keyboard navigation instructions for the interaction
     * @returns {object} Object containing keyboard navigation details
     */
    getKeyboardNavigationInstructions: () => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                enterKeyAction: undefined,
                tabKeyAction: undefined,
                altF10KeyAction: undefined,
                escKeyAction: undefined,
            };
        }

        return {
            enterKeyAction: t('interactions.extendedText.enterKeyAction'),
            tabKeyAction: t('interactions.extendedText.tabKeyAction'),
            altF10KeyAction: t('interactions.extendedText.altF10KeyAction'),
            escKeyAction: t('interactions.extendedText.escKeyAction'),
        };
    },

    isA11yV5AndAboveSupported: () => a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v5),
};

export default extendedTextA11ySelector;
